import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Lock, Loader2, ArrowLeft, Eye, EyeOff, CheckCircle, Home } from 'lucide-react';
import { tenantUrl } from '../../utils/tenantResolver';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PremiumResetPasswordPage({ tenantConfig }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const tenantSlug = tenantConfig?.slug || searchParams.get('tenant') || '';
  const tenantName = tenantConfig?.name || '';
  const logoUrl = tenantConfig?.logo_url || '';

  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ password: '', confirm: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (form.password !== form.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password: form.password });
      setDone(true);
      toast.success('Password reset successfully');
      const loginUrl = tenantUrl('/login', tenantSlug);
      setTimeout(() => navigate(loginUrl), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Reset failed');
    } finally {
      setLoading(false);
    }
  };

  const loginUrl = tenantUrl('/login', tenantSlug);

  return (
    <div className="prem-auth-page" data-testid="premium-reset-password-page">
      <div className="prem-auth-window">
        {/* Browser Chrome */}
        <div className="prem-auth-chrome">
          <div className="prem-auth-dots">
            <span className="prem-dot prem-dot-red" />
            <span className="prem-dot prem-dot-yellow" />
            <span className="prem-dot prem-dot-green" />
          </div>
        </div>

        {/* Blue gradient header */}
        <div className="prem-auth-header-bar">
          <div className="prem-auth-brand-wrap">
            {logoUrl ? (
              <img src={logoUrl} alt={tenantName} className="prem-auth-brand-logo" />
            ) : (
              <div className="prem-auth-brand-icon">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="white"><path d="M5 3l14 9-14 9V3z"/></svg>
              </div>
            )}
            <span className="prem-auth-brand-text">DepinaCRM</span>
          </div>
        </div>

        {/* White body */}
        <div className="prem-auth-body">
          {!done ? (
            <>
              <h1 className="prem-auth-welcome">Set New Password</h1>
              <p className="prem-auth-subtitle">Choose a strong password for your account.</p>

              {tenantSlug && (
                <div className="prem-auth-tenant-box" data-testid="premium-reset-tenant-badge">
                  <div className="prem-auth-tenant-row prem-auth-tenant-domain">
                    <Home size={18} />
                    <span>{tenantSlug}.depinacrm.com</span>
                  </div>
                  <div className="prem-auth-tenant-divider" />
                  <div className="prem-auth-tenant-row prem-auth-tenant-name">
                    <Home size={16} />
                    <span>{tenantName || tenantSlug}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className="prem-auth-form" data-testid="premium-reset-form">
                <div className="prem-auth-field">
                  <label>New Password</label>
                  <div className="prem-auth-input-wrap">
                    <Lock size={18} className="prem-auth-input-icon" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={form.password}
                      onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                      placeholder="Min 6 characters"
                      data-testid="premium-reset-password"
                    />
                    <button
                      type="button"
                      className="prem-auth-pw-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      data-testid="premium-reset-toggle-pw"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="prem-auth-field">
                  <label>Confirm Password</label>
                  <div className="prem-auth-input-wrap">
                    <Lock size={18} className="prem-auth-input-icon" />
                    <input
                      type="password"
                      value={form.confirm}
                      onChange={(e) => setForm((p) => ({ ...p, confirm: e.target.value }))}
                      placeholder="Confirm password"
                      data-testid="premium-reset-confirm"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="prem-auth-submit"
                  disabled={loading}
                  data-testid="premium-reset-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="animate-spin" size={18} />
                      Resetting...
                    </>
                  ) : (
                    'Reset Password'
                  )}
                </button>
              </form>
            </>
          ) : (
            <div className="prem-auth-success-block" data-testid="premium-reset-success">
              <CheckCircle size={52} className="prem-auth-success-check" />
              <h1 className="prem-auth-welcome">Password Reset!</h1>
              <p className="prem-auth-subtitle">Redirecting to sign in...</p>
            </div>
          )}

          <Link to={loginUrl} className="prem-auth-back-link" data-testid="premium-reset-back">
            <ArrowLeft size={16} /> Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
}
